class PasNewPopular extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.activeIndex = 0;
    this.sectionInView = true;
    this.resumeVideo = false;
    this.steps = [...this.querySelectorAll('[data-story-step]')];
    this.layers = [...this.querySelectorAll('[data-media-layer]')];
    this.panels = [...this.querySelectorAll('[data-story-panel]')];
    this.tabs = [...this.querySelectorAll('[data-story-tab]')];
    this.segments = [...this.querySelectorAll('[data-progress-segment]')];
    if (!this.tabs.length) return;

    this.addEventListener('click', (event) => {
      const videoToggle = event.target.closest('[data-video-toggle]');
      if (videoToggle && this.contains(videoToggle)) {
        this.toggleVideo(videoToggle.closest('[data-media-layer]'));
        return;
      }
      const tab = event.target.closest('[data-story-tab]');
      if (!tab || !this.contains(tab)) return;
      this.scrollToStory(Number(tab.dataset.index));
    });

    this.addEventListener('keydown', (event) => {
      const tab = event.target.closest('[data-story-tab]');
      if (!tab || !this.contains(tab)) return;
      const delta = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 0;
      if (!delta) return;
      event.preventDefault();
      const current = Number(tab.dataset.index);
      const next = (current + delta + this.tabs.length) % this.tabs.length;
      this.activate(next);
      this.tabs[next]?.focus();
      this.scrollToStory(next);
    });

    this.onVisibilityChange = () => this.syncPlayback();
    this.onBlockSelect = (event) => {
      const indexed = event.target?.closest?.('[data-index]');
      if (!indexed || !this.contains(indexed)) return;
      this.activate(Number(indexed.dataset.index));
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    this.setupStepObserver();
    this.setupSectionObserver();
    this.setupReveals();
    this.activate(0, true);
  }

  disconnectedCallback() {
    this.stepObserver?.disconnect();
    this.sectionObserver?.disconnect();
    this.revealObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.preloadLink?.remove();
  }

  setupStepObserver() {
    if (!('IntersectionObserver' in window)) return;
    this.stepObserver = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting);
      if (!visible.length) return;
      const centre = window.innerHeight / 2;
      visible.sort((a, b) => Math.abs(a.boundingClientRect.top + a.boundingClientRect.height / 2 - centre) - Math.abs(b.boundingClientRect.top + b.boundingClientRect.height / 2 - centre));
      this.activate(Number(visible[0].target.dataset.index));
    }, { rootMargin: '-49% 0px -49% 0px', threshold: 0 });
    this.steps.forEach((step) => this.stepObserver.observe(step));
  }

  setupSectionObserver() {
    if (!('IntersectionObserver' in window)) return;
    this.sectionObserver = new IntersectionObserver(([entry]) => {
      this.sectionInView = Boolean(entry?.isIntersecting);
      this.syncPlayback();
    }, { threshold: 0.05 });
    this.sectionObserver.observe(this);
  }

  setupReveals() {
    const nodes = [...this.querySelectorAll('[data-reveal]')];
    if (!nodes.length) return;
    this.classList.add('is-reveal-ready');
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach((node) => node.classList.add('is-revealed'));
      return;
    }
    this.revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        this.revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    nodes.forEach((node) => this.revealObserver.observe(node));
  }

  activate(index, force = false) {
    if (!Number.isInteger(index) || index < 0 || index >= this.tabs.length) return;
    if (!force && index === this.activeIndex) return;
    this.stopVideo();
    this.activeIndex = index;
    this.layers.forEach((layer, layerIndex) => {
      const active = layerIndex === index;
      layer.classList.toggle('is-active', active);
      layer.setAttribute('aria-hidden', String(!active));
    });
    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === index;
      panel.classList.toggle('is-active', active);
      panel.setAttribute('aria-hidden', String(!active));
      panel.querySelectorAll('a,button').forEach((control) => active ? control.removeAttribute('tabindex') : control.setAttribute('tabindex', '-1'));
    });
    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.setAttribute('aria-current', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });
    this.segments.forEach((segment, segmentIndex) => segment.classList.toggle('is-complete', segmentIndex <= index));
    const count = this.querySelector('[data-current-count]');
    if (count) count.textContent = String(index + 1).padStart(2, '0');
    const title = this.tabs[index]?.textContent?.trim() || 'featured product';
    const announcement = this.querySelector('[data-active-announcement]');
    if (announcement) announcement.textContent = `Showing ${title}, product ${index + 1} of ${this.tabs.length}`;
    this.preloadNextImage();
  }

  scrollToStory(index) {
    const step = this.steps[index];
    if (!step) {
      this.activate(index);
      return;
    }
    this.activate(index);
    const rect = step.getBoundingClientRect();
    const top = rect.top + window.scrollY + rect.height / 2 - window.innerHeight / 2;
    window.scrollTo({ top: Math.max(0, top), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  preloadNextImage() {
    this.preloadLink?.remove();
    const next = this.layers[(this.activeIndex + 1) % this.layers.length];
    if (!next?.dataset.preloadUrl) return;
    this.preloadLink = document.createElement('link');
    this.preloadLink.rel = 'preload';
    this.preloadLink.as = 'image';
    this.preloadLink.href = next.dataset.preloadUrl;
    document.head.append(this.preloadLink);
  }

  toggleVideo(layer) {
    if (!layer || !layer.classList.contains('is-active')) return;
    const video = layer.querySelector('[data-story-video]');
    if (!video) return;
    if (layer.classList.contains('is-playing')) {
      this.stopVideo();
      return;
    }
    this.stopVideo();
    layer.classList.add('is-playing');
    this.playingLayer = layer;
    this.resumeVideo = true;
    this.updateVideoButton(layer, true);
    if (this.sectionInView && !document.hidden) {
      video.play().catch(() => this.stopVideo());
    }
  }

  stopVideo() {
    this.layers?.forEach((layer) => {
      const video = layer.querySelector('[data-story-video]');
      if (video) {
        video.pause();
        try { video.currentTime = 0; } catch {}
      }
      layer.classList.remove('is-playing');
      this.updateVideoButton(layer, false);
    });
    this.playingLayer = null;
    this.resumeVideo = false;
  }

  syncPlayback() {
    const video = this.playingLayer?.querySelector('[data-story-video]');
    if (!video || !this.resumeVideo) return;
    if (document.hidden || !this.sectionInView) video.pause();
    else video.play().catch(() => this.stopVideo());
  }

  updateVideoButton(layer, playing) {
    const button = layer.querySelector('[data-video-toggle]');
    const label = layer.querySelector('[data-video-label]');
    const title = this.tabs?.[Number(layer.dataset.index)]?.textContent?.trim() || 'product';
    if (label) label.textContent = playing ? 'Stop video' : 'Watch it work';
    if (button) button.setAttribute('aria-label', `${playing ? 'Stop' : 'Play'} the ${title} demonstration video`);
  }
}

if (!customElements.get('pas-new-popular')) customElements.define('pas-new-popular', PasNewPopular);
